/**
 * NetworkService - Comprehensive network resilience layer for poor connectivity areas
 * 
 * Features:
 * - Online/offline detection with listeners
 * - Resilient fetch with retry and exponential backoff
 * - Offline upload queue with automatic sync
 * - Local data caching for offline viewing
 * - Connection quality detection
 */

// ============ NETWORK STATE ============
let isOnline = navigator.onLine;
let connectionQuality = 'good'; // 'good', 'slow', 'offline'
const listeners = new Set();

// Update online status
window.addEventListener('online', () => {
  isOnline = true;
  connectionQuality = 'good';
  listeners.forEach(cb => cb({ online: true, quality: connectionQuality }));
  processOfflineQueue(); // Auto-process queue when back online
});

window.addEventListener('offline', () => {
  isOnline = false;
  connectionQuality = 'offline';
  listeners.forEach(cb => cb({ online: false, quality: 'offline' }));
});

// Listen for service worker sync messages
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'SYNC_OFFLINE_QUEUE') {
      processOfflineQueue();
    }
  });
}

// ============ CONNECTION QUALITY ============
// Check connection quality (if Network Information API is available)
const checkConnectionQuality = () => {
  if ('connection' in navigator) {
    const conn = navigator.connection;
    const effectiveType = conn.effectiveType; // '4g', '3g', '2g', 'slow-2g'
    const downlink = conn.downlink; // Mbps
    
    if (!navigator.onLine) {
      connectionQuality = 'offline';
    } else if (effectiveType === 'slow-2g' || effectiveType === '2g' || downlink < 0.5) {
      connectionQuality = 'slow';
    } else if (effectiveType === '3g' || downlink < 1.5) {
      connectionQuality = 'slow';
    } else {
      connectionQuality = 'good';
    }
    
    return connectionQuality;
  }
  
  return isOnline ? 'good' : 'offline';
};

// Monitor connection changes
if ('connection' in navigator && navigator.connection) {
  navigator.connection.addEventListener('change', () => {
    const oldQuality = connectionQuality;
    checkConnectionQuality();
    if (oldQuality !== connectionQuality) {
      listeners.forEach(cb => cb({ online: isOnline, quality: connectionQuality }));
    }
  });
}

// ============ MAIN SERVICE ============
export const NetworkService = {
  // Check if online
  isOnline: () => isOnline,
  
  // Get connection quality
  getQuality: () => {
    checkConnectionQuality();
    return connectionQuality;
  },
  
  // Subscribe to network changes
  subscribe: (callback) => {
    listeners.add(callback);
    // Immediately call with current state
    callback({ online: isOnline, quality: checkConnectionQuality() });
    return () => listeners.delete(callback);
  },

  // Resilient fetch with retry and exponential backoff
  resilientFetch: async (url, options = {}, config = {}) => {
    const quality = checkConnectionQuality();
    
    // Adjust config based on connection quality
    const {
      maxRetries = quality === 'slow' ? 7 : 5,
      baseDelay = quality === 'slow' ? 2000 : 1000,
      maxDelay = 30000,
      timeout = quality === 'slow' ? 60000 : 30000,
      onRetry = null,
      requestId = null
    } = config;

    // Add request ID for idempotency if provided
    if (requestId) {
      options.headers = {
        ...options.headers,
        'X-Request-ID': requestId
      };
    }

    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Check if offline - queue instead of failing
        if (!navigator.onLine) {
          throw new Error('OFFLINE');
        }

        // Add timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok && response.status >= 500) {
          throw new Error(`Server error: ${response.status}`);
        }
        
        return response;
        
      } catch (error) {
        lastError = error;
        
        if (error.name === 'AbortError') {
          // Timeout - retry with longer timeout
        } else if (error.message === 'OFFLINE') {
          break;
        } else if (error.message?.includes('4')) {
          throw error;
        }
        
        if (attempt < maxRetries) {
          const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
          const jitter = delay * 0.2 * Math.random();
          
          if (onRetry) {
            onRetry(attempt + 1, maxRetries, delay);
          }
          
          await new Promise(resolve => setTimeout(resolve, delay + jitter));
        }
      }
    }
    
    throw lastError;
  }
};

// ============ OFFLINE QUEUE ============
const QUEUE_KEY = 'offline_upload_queue';

const getQueue = () => {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
};

const saveQueue = (queue) => {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
};

// Add item to offline queue
export const queueOfflineUpload = (item) => {
  const queue = getQueue();
  const queueItem = {
    id: item.requestId || `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    url: item.url,
    method: item.method || 'POST',
    headers: item.headers || {},
    body: item.body,
    type: item.type || 'upload',
    metadata: item.metadata || {},
    createdAt: new Date().toISOString(),
    retryCount: 0,
    status: 'pending'
  };
  
  queue.push(queueItem);
  saveQueue(queue);
  
  console.log('📥 Queued offline:', queueItem.type, queueItem.id);
  
  // Dispatch event for UI update
  window.dispatchEvent(new CustomEvent('queue-updated', { 
    detail: getQueueStatus() 
  }));
  
  return queueItem.id;
};

// Process offline queue when online
export const processOfflineQueue = async () => {
  if (!navigator.onLine) return { processed: 0, failed: 0, remaining: 0 };
  
  const queue = getQueue();
  if (queue.length === 0) return { processed: 0, failed: 0, remaining: 0 };
  
  console.log('🔄 Processing offline queue:', queue.length, 'items');
  
  // Dispatch syncing event
  window.dispatchEvent(new CustomEvent('queue-syncing', { 
    detail: { total: queue.length } 
  }));
  
  const processed = [];
  const failed = [];
  
  for (const item of queue) {
    if (item.status === 'processing') continue;
    
    try {
      item.status = 'processing';
      saveQueue(queue);
      
      const response = await NetworkService.resilientFetch(item.url, {
        method: item.method,
        headers: {
          'Content-Type': 'application/json',
          ...item.headers,
          'X-Request-ID': item.id
        },
        body: item.body
      }, {
        maxRetries: 3,
        requestId: item.id
      });
      
      if (response.ok) {
        processed.push(item.id);
        console.log('✅ Queue item synced:', item.type, item.id);
      } else {
        throw new Error(`Failed: ${response.status}`);
      }
      
    } catch (error) {
      console.error('❌ Queue item failed:', item.id, error.message);
      item.retryCount++;
      item.status = 'pending';
      item.lastError = error.message;
      
      if (item.retryCount >= 5) {
        item.status = 'failed';
        failed.push(item.id);
      }
    }
  }
  
  // Remove processed items, keep failed/pending
  const remaining = queue.filter(item => 
    !processed.includes(item.id) && item.status !== 'failed'
  );
  saveQueue(remaining);
  
  // Dispatch completion event
  window.dispatchEvent(new CustomEvent('queue-synced', { 
    detail: { processed: processed.length, failed: failed.length, remaining: remaining.length } 
  }));
  
  return { processed: processed.length, failed: failed.length, remaining: remaining.length };
};

// Get queue status
export const getQueueStatus = () => {
  const queue = getQueue();
  return {
    total: queue.length,
    pending: queue.filter(i => i.status === 'pending').length,
    processing: queue.filter(i => i.status === 'processing').length,
    failed: queue.filter(i => i.status === 'failed').length,
    items: queue
  };
};

// Clear failed items
export const clearFailedItems = () => {
  const queue = getQueue().filter(i => i.status !== 'failed');
  saveQueue(queue);
  window.dispatchEvent(new CustomEvent('queue-updated', { detail: getQueueStatus() }));
};

// Remove specific item from queue
export const removeQueueItem = (itemId) => {
  const queue = getQueue().filter(i => i.id !== itemId);
  saveQueue(queue);
  window.dispatchEvent(new CustomEvent('queue-updated', { detail: getQueueStatus() }));
};

// ============ LOCAL DATA CACHE ============
const DATA_CACHE_KEY = 'offline_data_cache';
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes (reduced from 24 hours for freshness)

const getDataCache = () => {
  try {
    return JSON.parse(localStorage.getItem(DATA_CACHE_KEY) || '{}');
  } catch {
    return {};
  }
};

const saveDataCache = (cache) => {
  try {
    localStorage.setItem(DATA_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    // localStorage might be full, clear old entries
    console.warn('Cache storage full, clearing old entries');
    clearExpiredCache();
  }
};

// Cache data for offline viewing
export const cacheData = (key, data, expiryMs = CACHE_EXPIRY) => {
  const cache = getDataCache();
  cache[key] = {
    data,
    timestamp: Date.now(),
    expiry: Date.now() + expiryMs
  };
  saveDataCache(cache);
};

// Get cached data
export const getCachedData = (key) => {
  const cache = getDataCache();
  const entry = cache[key];
  
  if (!entry) return null;
  
  // Check if expired
  if (Date.now() > entry.expiry) {
    delete cache[key];
    saveDataCache(cache);
    return null;
  }
  
  return {
    data: entry.data,
    timestamp: entry.timestamp,
    isStale: Date.now() - entry.timestamp > 5 * 60 * 1000 // 5 minutes
  };
};

// Clear expired cache entries
export const clearExpiredCache = () => {
  const cache = getDataCache();
  const now = Date.now();
  let cleared = 0;
  
  Object.keys(cache).forEach(key => {
    if (now > cache[key].expiry) {
      delete cache[key];
      cleared++;
    }
  });
  
  if (cleared > 0) {
    saveDataCache(cache);
    console.log(`🧹 Cleared ${cleared} expired cache entries`);
  }
};

// Clear all cached data
export const clearAllCache = () => {
  localStorage.removeItem(DATA_CACHE_KEY);
};

// ============ FETCH WITH CACHE ============
// Fetch data with automatic caching for offline use
export const fetchWithCache = async (url, options = {}, cacheKey = null) => {
  const key = cacheKey || url;
  const cached = getCachedData(key);
  
  // If offline, return cached data
  if (!navigator.onLine) {
    if (cached) {
      console.log('📦 Returning cached data (offline):', key);
      return { 
        data: cached.data, 
        fromCache: true, 
        isStale: true,
        offline: true
      };
    }
    throw new Error('OFFLINE_NO_CACHE');
  }
  
  try {
    const response = await NetworkService.resilientFetch(url, options);
    
    if (response.ok) {
      const data = await response.json();
      
      // Cache the fresh data
      cacheData(key, data);
      
      return { 
        data, 
        fromCache: false, 
        isStale: false,
        offline: false
      };
    }
    
    throw new Error(`HTTP ${response.status}`);
    
  } catch (error) {
    // If network failed but we have cache, return it
    if (cached) {
      console.log('📦 Returning cached data (network error):', key);
      return { 
        data: cached.data, 
        fromCache: true, 
        isStale: cached.isStale,
        offline: false,
        error: error.message
      };
    }
    
    throw error;
  }
};

// Process queue on page load if online
if (navigator.onLine) {
  setTimeout(processOfflineQueue, 2000);
}

// Clear expired cache periodically
setInterval(clearExpiredCache, 60 * 60 * 1000); // Every hour

export default NetworkService;
